import { createContext, useContext, useState, useCallback, useRef, useEffect, type ReactNode } from "react"
import type { Campaign, FollowUp, MessageTemplate, CampaignContentContextType, AbTestSettings } from "./types"
import {
  updateFollowUp as apiUpdateFollowUp,
  createFollowUp as apiCreateFollowUp,
  deleteFollowUp as apiDeleteFollowUp,
  duplicateFollowUp as apiDuplicateFollowUp,
  fetchFollowUps as apiFetchFollowUps,
  updateAbTestSettings as apiUpdateAbTestSettings,
} from "./api"
import { t } from "./i18n"

const AUTOSAVE_DEBOUNCE_MS = 500

interface CampaignContentProviderProps {
  children: ReactNode
  campaign: Campaign
  followUps: FollowUp[]
  editable: boolean
  canAddFollowUp: boolean
  maxFollowUps: number
  messageTemplates: MessageTemplate[]
  allowEmailAttachments: boolean
  allowEmailImages: boolean
  allowVideoLinks: boolean
  currentUserEmail: string
}

const CampaignContentContext = createContext<CampaignContentContextType | undefined>(undefined)

export function CampaignContentProvider({
  children,
  campaign,
  followUps: initialFollowUps,
  editable,
  canAddFollowUp: initialCanAddFollowUp,
  maxFollowUps,
  messageTemplates,
  allowEmailAttachments,
  allowEmailImages,
  allowVideoLinks,
  currentUserEmail,
}: CampaignContentProviderProps) {
  const [followUps, setFollowUps] = useState<FollowUp[]>(initialFollowUps)
  const [selectedStep, setSelectedStep] = useState(0)
  const [isSaving, setIsSaving] = useState(false)
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [abTestSettings, setAbTestSettings] = useState<AbTestSettings | null>(campaign.ab_test_settings)

  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Cleanup debounce timeout on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current)
      }
    }
  }, [])

  // Only count Variant A follow-ups for the limit (Variant B are sibling copies)
  const variantAFollowUps = followUps.filter((fu) => fu.variant === "A" || !fu.variant)
  const canAddFollowUp = initialCanAddFollowUp && variantAFollowUps.length < maxFollowUps

  const updateFollowUp = useCallback(
    (followUpId: number, updates: Partial<FollowUp>) => {
      setFollowUps((prev) =>
        prev.map((followUp) => (followUp.id === followUpId ? { ...followUp, ...updates } : followUp)),
      )

      setSaveError(null)

      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current)
      }

      saveTimeoutRef.current = setTimeout(async () => {
        setIsSaving(true)

        const { id: _id, variant: _variant, ...updateParams } = updates
        const result = await apiUpdateFollowUp({
          campaignId: campaign.id,
          followUpId,
          ...updateParams,
        })

        setIsSaving(false)

        if (result.success) {
          setLastSavedAt(new Date())
          setSaveError(null)
        } else {
          setSaveError(result.error || t("js.campaigns.content.save_error"))
        }
      }, AUTOSAVE_DEBOUNCE_MS)
    },
    [campaign.id],
  )

  const addFollowUp = useCallback(async () => {
    if (!canAddFollowUp) return

    // New step is based on number of Variant A follow-ups
    const newStep = variantAFollowUps.length
    setIsSaving(true)

    const result = await apiCreateFollowUp({
      campaignId: campaign.id,
      step: newStep,
    })

    if (result.success && result.followUp) {
      const newFollowUp = result.followUp
      setFollowUps((prev) => [...prev, newFollowUp])
      setSelectedStep(newStep)
      setLastSavedAt(new Date())
      setSaveError(null)
    } else {
      setSaveError(result.error || t("js.campaigns.content.add_error"))
    }

    setIsSaving(false)
  }, [campaign.id, canAddFollowUp, variantAFollowUps.length])

  const deleteFollowUpHandler = useCallback(
    async (step: number) => {
      const followUpToDelete = followUps.find((followUp) => followUp.step === step)
      if (!followUpToDelete?.deletable) return

      setIsSaving(true)

      const result = await apiDeleteFollowUp({
        campaignId: campaign.id,
        step,
      })

      if (result.success) {
        try {
          const updatedFollowUps = await apiFetchFollowUps(campaign.id)
          setFollowUps(updatedFollowUps)

          // Adjust selected step if needed - use functional update to avoid stale closure
          setSelectedStep((prev) => (prev >= step && prev > 0 ? prev - 1 : prev))

          setLastSavedAt(new Date())
          setSaveError(null)
        } catch {
          setSaveError(t("js.campaigns.content.delete_error"))
        }
      } else {
        setSaveError(result.error || t("js.campaigns.content.delete_error"))
      }

      setIsSaving(false)
    },
    [campaign.id, followUps],
  )

  const duplicateFollowUpHandler = useCallback(
    async (step: number) => {
      if (!canAddFollowUp) return

      setIsSaving(true)

      const result = await apiDuplicateFollowUp({
        campaignId: campaign.id,
        step,
      })

      if (result.success && result.followUp) {
        try {
          // Refetch all follow-ups since the backend shifts steps when inserting
          const updatedFollowUps = await apiFetchFollowUps(campaign.id)
          setFollowUps(updatedFollowUps)
          setSelectedStep(result.followUp.step)
          setLastSavedAt(new Date())
          setSaveError(null)
        } catch {
          setSaveError(t("js.campaigns.content.duplicate_error"))
        }
      } else {
        setSaveError(result.error || t("js.campaigns.content.duplicate_error"))
      }

      setIsSaving(false)
    },
    [campaign.id, canAddFollowUp],
  )

  const createVariantBHandler = useCallback(
    async (step: number, sourceFollowUp: FollowUp) => {
      setIsSaving(true)

      const result = await apiCreateFollowUp({
        campaignId: campaign.id,
        step,
        variant: "B",
        subject: sourceFollowUp.subject,
        body: sourceFollowUp.body,
        wait_days: sourceFollowUp.wait_days,
      })

      if (result.success && result.followUp) {
        const newFollowUp = result.followUp
        // Optimistic update:
        // 1. Update Variant A: set variant to "A" and ab_testing_enabled to true
        // 2. Add the new Variant B
        setFollowUps((prev) => [
          ...prev.map((fu) =>
            fu.step === step && (fu.variant === null || fu.variant === "A")
              ? { ...fu, variant: "A" as const, ab_testing_enabled: true }
              : fu,
          ),
          newFollowUp,
        ])
        setLastSavedAt(new Date())
        setSaveError(null)
      } else {
        setSaveError(result.error || t("js.campaigns.content.save_error"))
      }

      setIsSaving(false)
    },
    [campaign.id],
  )

  const deleteVariantBHandler = useCallback(
    async (step: number) => {
      setIsSaving(true)

      const result = await apiDeleteFollowUp({
        campaignId: campaign.id,
        step,
        variant: "B",
      })

      if (result.success) {
        // Optimistic update:
        // 1. Remove Variant B
        // 2. Reset Variant A: set variant to null and ab_testing_enabled to false
        setFollowUps((prev) =>
          prev
            .filter((fu) => !(fu.step === step && fu.variant === "B"))
            .map((fu) =>
              fu.step === step && fu.variant === "A" ? { ...fu, variant: null, ab_testing_enabled: false } : fu,
            ),
        )
        setLastSavedAt(new Date())
        setSaveError(null)
      } else {
        setSaveError(result.error || t("js.campaigns.content.delete_error"))
      }

      setIsSaving(false)
    },
    [campaign.id],
  )

  const updateAbTestSettingsHandler = useCallback(
    async (variantAPercentage: number) => {
      setIsSaving(true)

      const result = await apiUpdateAbTestSettings({
        campaignId: campaign.id,
        variantAPercentage,
      })

      if (result.success && result.variant_a_percentage !== undefined) {
        const newPercentage = result.variant_a_percentage
        setAbTestSettings((prev) =>
          prev
            ? { ...prev, variant_a_percentage: newPercentage }
            : { variant_a_percentage: newPercentage, winner_variant: null, winner_selected_at: null },
        )
        setLastSavedAt(new Date())
        setSaveError(null)
      } else {
        setSaveError(result.error || t("js.campaigns.content.save_error"))
      }

      setIsSaving(false)
    },
    [campaign.id],
  )

  const value: CampaignContentContextType = {
    campaign,
    followUps,
    selectedStep,
    setSelectedStep,
    editable,
    canAddFollowUp,
    maxFollowUps,
    messageTemplates,
    allowEmailAttachments,
    allowEmailImages,
    allowVideoLinks,
    currentUserEmail,
    updateFollowUp,
    addFollowUp,
    deleteFollowUp: deleteFollowUpHandler,
    duplicateFollowUp: duplicateFollowUpHandler,
    isSaving,
    lastSavedAt,
    saveError,
    abTestSettings,
    updateAbTestSettings: updateAbTestSettingsHandler,
    createVariantB: createVariantBHandler,
    deleteVariantB: deleteVariantBHandler,
  }

  return <CampaignContentContext.Provider value={value}>{children}</CampaignContentContext.Provider>
}

export function useCampaignContent() {
  const context = useContext(CampaignContentContext)
  if (context === undefined) {
    throw new Error("useCampaignContent must be used within a CampaignContentProvider")
  }
  return context
}
